import { useState, useEffect } from "react";
import { Input } from "~/components/ui/input";
import type { ComponentProps } from "react";

type NumberInputProps = Omit<
  ComponentProps<typeof Input>,
  "value" | "onChange" | "onBlur" | "type" | "inputMode" | "pattern"
> & {
  value?: number;
  onChange?: (value: number | undefined) => void;
  onBlur?: (value: number | undefined) => void;
  suffix?: string;
  numberControls?: boolean;
  onIncrease?: () => void;
  onDecrease?: () => void;
  maxValue?: number;
  allowEmpty?: boolean; // If true, empty field returns undefined instead of 0
  allowDecimals?: boolean; // If true, allows decimal numbers with 2 decimal places (German format: comma as separator)
};

/** Parse German-style input: strip thousand separators (.), then treat comma as decimal */
function parseGermanNumber(input: string): number {
  const withoutThousands = input.replace(/\./g, "");
  const normalized = withoutThousands.replace(",", ".");
  return Number(normalized) || 0;
}

/** Format a number as German decimal: comma for decimals (e.g. 1234.56 → "1234,56") */
function formatGermanDecimal(value: number, decimals: number = 2): string {
  const fixed = value.toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  return decPart ? `${intPart},${decPart}` : intPart;
}

export function NumberInput({
  value,
  onChange,
  onBlur,
  suffix,
  numberControls,
  maxValue,
  allowEmpty = false,
  allowDecimals = false,
  ...props
}: NumberInputProps) {
  const [localValue, setLocalValue] = useState<string>(
    value !== undefined
      ? allowDecimals
        ? formatGermanDecimal(value)
        : String(value)
      : ""
  );
  const [isFocused, setIsFocused] = useState(false);

  // Sync local state when prop changes (from external updates)
  useEffect(() => {
    if (!isFocused) {
      if (value !== undefined) {
        setLocalValue(
          allowDecimals ? formatGermanDecimal(value) : String(value)
        );
      } else {
        setLocalValue("");
      }
    }
  }, [value, allowDecimals, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (allowDecimals) {
      // Allow: digits, period as thousand separator, one comma, max 2 decimal places
      const regex = /^[\d.]*(,\d{0,2})?$/;
      if (!regex.test(raw) && raw !== "") return;

      if (raw === "" || raw === ",") {
        setLocalValue(raw);
        if (allowEmpty) {
          onChange?.(undefined);
        } else {
          onChange?.(0);
        }
        return;
      }

      const num = parseGermanNumber(raw);
      const clampedValue = maxValue !== undefined ? Math.min(num, maxValue) : num;
      setLocalValue(raw);
      onChange?.(clampedValue);
    } else {
      // Only allow digits
      if (!/^\d*$/.test(raw)) return;

      if (raw === "") {
        setLocalValue("");
        if (allowEmpty) {
          onChange?.(undefined);
        } else {
          onChange?.(0);
        }
        return;
      }

      const num = Number(raw);
      const clampedValue = maxValue !== undefined ? Math.min(num, maxValue) : num;

      // If value was clamped, update local value to show the clamped value
      if (clampedValue !== num) {
        setLocalValue(String(clampedValue));
      } else {
        setLocalValue(raw);
      }

      onChange?.(clampedValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Select all text when focused
    e.target.select();
  };

  const handleBlur = () => {
    setIsFocused(false);

    if (allowDecimals) {
      const inputValue = localValue;
      if (inputValue === "" || inputValue === ",") {
        if (allowEmpty) {
          setLocalValue("");
          onBlur?.(undefined);
          if (onChange) {
            onChange(undefined);
          }
        } else {
          setLocalValue("0");
          onBlur?.(0);
          if (onChange) {
            onChange(0);
          }
        }
        return;
      }

      let toParse = inputValue;
      if (toParse.endsWith(",")) {
        toParse = toParse + "00";
      }

      const num = Math.max(0, parseGermanNumber(toParse));
      const clampedValue = maxValue !== undefined ? Math.min(num, maxValue) : num;

      setLocalValue(formatGermanDecimal(clampedValue));
      onBlur?.(clampedValue);
      if (onChange && clampedValue !== value) {
        onChange(clampedValue);
      }
    } else {
      // Bei allowEmpty: "" oder "0" als "leer" behandeln
      const isEmpty =
        localValue === "" || (allowEmpty && Number(localValue) === 0);

      if (isEmpty) {
        if (allowEmpty) {
          setLocalValue("");
          onBlur?.(undefined);
          // Auch Parent-State auf undefined setzen, nur wenn value leer ist
          if (onChange && value === undefined) {
            onChange(undefined);
          }
          return;
        } else {
          setLocalValue("0");
          onBlur?.(0);
          // Also call onChange to ensure parent state is updated
          if (onChange && value !== 0) {
            onChange(0);
          }
          return;
        }
      }

      let finalValue =
        typeof value === "number" ? value : Number(localValue) || 0;

      // Clamp to maxValue if provided
      if (maxValue !== undefined) {
        finalValue = Math.min(finalValue, maxValue);
      }

      setLocalValue(String(finalValue));
      onBlur?.(finalValue);
      // Also call onChange to ensure parent state is updated
      if (onChange && finalValue !== value) {
        onChange(finalValue);
      }
    }
  };

  const handleIncrease = () => {
    const currentValue =
      typeof value === "number"
        ? value
        : localValue === ""
          ? 0
          : allowDecimals
            ? parseGermanNumber(localValue)
            : Number(localValue) || 0;
    let newValue = currentValue + (allowDecimals ? 0.01 : 1);

    // Clamp to maxValue if provided
    if (maxValue !== undefined) {
      newValue = Math.min(newValue, maxValue);
    }

    if (allowDecimals) {
      setLocalValue(formatGermanDecimal(newValue));
    } else {
      setLocalValue(String(newValue));
    }
    onChange?.(newValue);
  };

  const handleDecrease = () => {
    const currentValue =
      typeof value === "number"
        ? value
        : localValue === ""
          ? 0
          : allowDecimals
            ? parseGermanNumber(localValue)
            : Number(localValue) || 0;
    const newValue = Math.max(0, currentValue - (allowDecimals ? 0.01 : 1));
    if (allowDecimals) {
      setLocalValue(formatGermanDecimal(newValue));
    } else {
      setLocalValue(String(newValue));
    }
    onChange?.(newValue);
  };

  return (
    <Input
      type="text"
      inputMode={allowDecimals ? "decimal" : "numeric"}
      pattern={allowDecimals ? undefined : "[0-9]*"}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      suffix={suffix}
      numberControls={numberControls}
      onIncrease={numberControls ? handleIncrease : undefined}
      onDecrease={numberControls ? handleDecrease : undefined}
      {...props}
    />
  );
}
